import contractHashes from '../json/contractHashes.json';

export const LP_POOL_TYPES = new Set([
    'dedust_pool',
    'stonfi_pool',
    'stonfi_pool_v2',
]);

// STON.fi stores native TON as the zero address or as their pTON jetton master.
const NATIVE_TON_ADDRESSES = new Set([
    'EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c',
    'EQCM3B12QK1e4yZSf8GtBRT0aLMNyEsBc_DhVfRRtOEffLez', // STON.fi pTON v1
    'EQBnGWMCf3-FZZq1W4IWcWiGAc3PHuZ0_H-7sad2oY00o83S', // STON.fi pTON v2
]);

const STABLE_JETTONS = new Set([
    'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs', // USDT
]);

export const TON_NATIVE_ASSET = Object.freeze({
    type: 'native',
    address: null,
    symbol: 'TON',
    name: 'Toncoin',
    decimals: 9,
    image: 'https://assets.dedust.io/images/ton.webp',
});

/**
 * @param  {String} codeHash
 * @return {String|null} contractHashes type, or null if this is not an LP pool
 */
export const getLpPoolType = function getLpPoolTypeByCodeHash(codeHash) {
    const type = contractHashes[codeHash];
    return LP_POOL_TYPES.has(type) ? type : null;
};

/**
 * @param  {String} poolType
 * @return {'dedust'|'stonfi'|null}
 */
export const getLpDex = function getLpDexByPoolType(poolType) {
    if (poolType === 'dedust_pool') {
        return 'dedust';
    }

    if (poolType === 'stonfi_pool' || poolType === 'stonfi_pool_v2') {
        return 'stonfi';
    }

    return null;
};

// Toncenter labels a DEX action by protocol version, the pools themselves
// carry no interfaces there, so the action is what names the pool type:
const LP_POOL_TYPE_BY_DEX = Object.freeze({
    stonfi: 'stonfi_pool',
    stonfi_v2: 'stonfi_pool_v2',
    dedust: 'dedust_pool',
    dedust_v2: 'dedust_pool',
});

/**
 * @param  {String} dex  dex field of a Toncenter DEX action
 * @return {String|null} contractHashes type, or null for DEXes without an LP page
 */
export const getLpPoolTypeByDex = function getLpPoolTypeByDex(dex) {
    return LP_POOL_TYPE_BY_DEX[dex] || null;
};

// Anyone can point a .ton domain at any address, and scammers point theirs at
// the contracts every swap passes through ("bidask-scammer.ton" on a STON.fi
// router). The address page therefore shows STON.fi routers and DeDust vaults
// without a domain. Toncenter names the contract in its address book
// (interfaces, v1 routers and vaults only), the code hash covers v2 routers:
const DOMAINLESS_CONTRACT_TYPES = new Set([
    'stonfi_router',
    'dedust_vault',
]);

/**
 * @param  {Array<String>} [interfaces]  Toncenter address_book entry interfaces
 * @param  {String} [codeHash]
 * @return {Boolean}
 */
export const hidesDomain = function hidesDomainOfDexContract(interfaces, codeHash) {
    return DOMAINLESS_CONTRACT_TYPES.has(contractHashes[codeHash])
        || (Array.isArray(interfaces) && interfaces.some(name => DOMAINLESS_CONTRACT_TYPES.has(name)));
};

export const isNativeTonAddress = function isNativeTonAddress(address) {
    return !address || NATIVE_TON_ADDRESSES.has(address);
};

export const isStableJetton = function isStableJettonAddress(address) {
    return Boolean(address) && STABLE_JETTONS.has(address);
};

/**
 * Pair chart should follow the volatile side, not the LP token. TON/USDT
 * therefore charts TON; TON/NOT charts NOT.
 *
 * @param  {Object} asset0
 * @param  {Object} asset1
 * @return {Object}
 */
export const pickChartAsset = function pickVolatilePairAsset(asset0, asset1) {
    const isTon = asset => asset?.type === 'native' || isNativeTonAddress(asset?.address);
    const isStable = asset => isStableJetton(asset?.address);

    if (isTon(asset0) && asset1?.type === 'jetton' && !isStable(asset1)) {
        return asset1;
    }

    if (isTon(asset1) && asset0?.type === 'jetton' && !isStable(asset0)) {
        return asset0;
    }

    if (isTon(asset0) || isTon(asset1)) {
        return TON_NATIVE_ASSET;
    }

    if (asset0 && !isStable(asset0)) {
        return asset0;
    }

    if (asset1 && !isStable(asset1)) {
        return asset1;
    }

    return asset0 || asset1;
};

const assetSwapSlug = function assetToSwapSlug(asset) {
    return asset?.type === 'native' || isNativeTonAddress(asset?.address)
        ? 'TON'
        : asset?.address;
};

/**
 * The buy buttons under the pool card are an ad block, so every pool links
 * to DeDust regardless of which DEX actually hosts it.
 *
 * @param  {Object} asset0
 * @param  {Object} asset1
 * @return {String}
 */
export const getDedustSwapUrl = function getDedustSwapUrl(asset0, asset1) {
    const left = assetSwapSlug(asset0);
    const right = assetSwapSlug(asset1);

    if (!left || !right) {
        return 'https://dedust.io/swap';
    }

    return `https://dedust.io/swap/${left}/${right}`;
};
